import { Asset, AssetFilterOptions } from "@hydrologiq/gql-experience/graphql"
import { createSlice } from "@reduxjs/toolkit"
import { PayloadAction } from "@reduxjs/toolkit"
import { PaginationMeta } from "@hydrologiq/shared/types/pagination"

interface AssetsState {
  value: Asset[] | undefined
  pagination: PaginationMeta | undefined
  filterOptions: AssetFilterOptions | undefined
  loading: boolean
  error: string | null
}

const initialState = {
  value: undefined,
  pagination: undefined,
  filterOptions: undefined,
  loading: false,
  error: null,
} satisfies AssetsState as AssetsState

export const assetsSlice = createSlice({
  name: "assets",
  initialState,
  selectors: {
    getAssets: (state): Asset[] => {
      return state.value || []
    },
    getAssetById: (state, assetId: string | undefined): Asset | undefined => {
      return assetId ? state.value?.find((asset) => asset.id === assetId) : undefined
    },
    getAssetsPagination: (state): PaginationMeta | undefined => {
      return state.pagination
    },
    getAssetsFilterOptions: (state): FilterOptions | undefined => {
      return state.filterOptions
    },
    getAssetsLoading: (state): boolean => {
      return state.loading
    },
    getAssetsError: (state): string | null => {
      return state.error
    },
  },
  reducers: {
    updateAsset: (state, action: PayloadAction<Asset>) => {
      state.value = state.value?.map((asset) => (asset.id === action.payload.id ? action.payload : asset))
    },
    addAsset: (state, action: PayloadAction<Asset>) => {
      // DEPRECATED: This action should not be used with paginated state
      // When creating assets, navigate to the asset page instead of adding to paginated list
      // This avoids breaking pagination state
      state.value = [action.payload, ...(state.value || [])]
      // Note: Pagination metadata is intentionally not updated to prevent state corruption
    },
    setAssets: (state, action: PayloadAction<Asset[]>) => {
      state.value = action.payload
    },
    setAssetsWithPagination: (state, action: PayloadAction<{ assets: Asset[]; pagination: PaginationMeta }>) => {
      state.value = action.payload.assets
      state.pagination = action.payload.pagination
    },
    setAssetsFilterOptions: (state, action: PayloadAction<FilterOptions | undefined>) => {
      state.filterOptions = action.payload
    },
    setAssetsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setAssetsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    removeAsset: (state, action: PayloadAction<string>) => {
      state.value = state.value?.filter((asset) => asset.id !== action.payload)
      // Update pagination metadata when removing an asset
      if (state.pagination) {
        state.pagination.total_count = Math.max(0, state.pagination.total_count - 1)
        // Recalculate total pages based on new count
        state.pagination.total_pages = Math.ceil(state.pagination.total_count / state.pagination.per_page)
        // Update has_next if we now have fewer pages
        if (state.pagination.page >= state.pagination.total_pages) {
          state.pagination.has_next = false
        }
        // If we're on a page that no longer exists, ensure we don't show it
        if (state.pagination.page > state.pagination.total_pages && state.pagination.total_pages > 0) {
          state.pagination.page = state.pagination.total_pages
          state.pagination.has_prev = state.pagination.page > 1
        }
      }
    },
  },
})

export const {
  updateAsset,
  setAssets,
  setAssetsWithPagination,
  setAssetsFilterOptions,
  setAssetsLoading,
  setAssetsError,
  addAsset,
  removeAsset,
} = assetsSlice.actions

export const {
  getAssets,
  getAssetById,
  getAssetsPagination,
  getAssetsFilterOptions,
  getAssetsLoading,
  getAssetsError,
} = assetsSlice.selectors
export default assetsSlice.reducer
